import { assertStaffCapability, assertStaffRelationship, isSupervisor, transitionActorId, } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { resolveEligibleFixedPrice } from "./catalog-pricing.js";
import { conciergeRequestTerminalStates } from "./concierge-request.js";
import { DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
import { currencyCode, minorUnits } from "./money.js";
import { plannedPaymentRequestDraft } from "./payment-draft.js";
import { checkExpectedVersion, denied, isMember, requireReason, violation, } from "./transition-support.js";
// Frozen: the database CHECK constraints are generated from these tuples. Phase 2.5 (CHT-703):
// the provider-neutral PaymentRequest and Payment trust model. There is no provider SDK, order,
// webhook or checkout here: a provider is evidence, never aggregate authority.
export const paymentRequestStatuses = Object.freeze([
    "DRAFT",
    "READY",
    "PENDING_PAYMENT",
    "PAID",
    "PARTIALLY_REFUNDED",
    "REFUNDED",
    "FAILED",
    "EXPIRED",
    "CANCELLED",
]);
/** Final: no state change is allowed out of these. A FAILED request stays as evidence (a retry is a new request). */
export const paymentRequestTerminalStatuses = Object.freeze([
    "FAILED",
    "EXPIRED",
    "CANCELLED",
    "REFUNDED",
]);
/** Where the amount of a payment request comes from. Never a caller. */
export const paymentSourceTypes = Object.freeze([
    "CATALOG_FIXED_PRICE",
    "CONCIERGE_QUOTE",
]);
export const paymentQuoteStatuses = Object.freeze(["APPROVED", "WITHDRAWN"]);
/** Why the approver could approve: the request owner, or a supervisor. Owner decision 5: no second approver. */
export const paymentQuoteApprovalBases = Object.freeze(["OWNER", "SUPERVISOR"]);
export const supplierEvidenceKinds = Object.freeze([
    "SUPPLIER_EMAIL",
    "SUPPLIER_PORTAL",
    "SUPPLIER_CALL",
    "SUPPLIER_DOCUMENT",
]);
export const paymentObservationKinds = Object.freeze([
    "AUTHORIZED",
    "SETTLED",
    "ATTEMPT_FAILED",
    "FAILED",
    "ORDER_EXPIRED",
    "REFUND_SETTLED",
    "REFUND_FAILED",
]);
/** How the observation was authenticated. An unauthenticated delivery never reaches Postgres. */
export const paymentSignatureVerifications = Object.freeze(["VERIFIED", "API_FETCHED"]);
export const paymentObservationDecisions = Object.freeze([
    "APPLIED",
    "NO_TRANSITION",
    "DUPLICATE",
    "EXCEPTION",
]);
export const reconciliationExceptionKinds = Object.freeze([
    "DUPLICATE_CONFLICT",
    "UNKNOWN_ORDER",
    "ORDER_MISMATCH",
    "CUSTOMER_MISMATCH",
    "AMOUNT_MISMATCH",
    "CURRENCY_MISMATCH",
    "LATE_SETTLEMENT",
    "REQUEST_WITHDRAWN",
    "UNEXPECTED_STATE",
    "UNKNOWN_REFUND",
    "REFUND_MISMATCH",
]);
/**
 * The open exceptions that mean a capture may be held for a payment request that is not settled (the offer was withdrawn, the order or
 * the customer did not match, the amount or the currency differed, or the state was unexpected). While one is OPEN on a request, no
 * automatic expiry runs for it: a settled payment is never overridden by expiry, and a supervisor resolves the exception first.
 * `DUPLICATE_CONFLICT`, `UNKNOWN_*` and `REFUND_MISMATCH` hold no capture of this request, and `LATE_SETTLEMENT` is raised after the end.
 */
export const settlementHoldingExceptionKinds = Object.freeze([
    "REQUEST_WITHDRAWN",
    "ORDER_MISMATCH",
    "CUSTOMER_MISMATCH",
    "AMOUNT_MISMATCH",
    "CURRENCY_MISMATCH",
    "UNEXPECTED_STATE",
]);
export const reconciliationExceptionStatuses = Object.freeze(["OPEN", "RESOLVED"]);
export const reconciliationResolutionCodes = Object.freeze([
    "REFUNDED_OUT_OF_BAND",
    "CONFIRMED_NO_MONEY_MOVED",
    "ADOPTED_REFUND",
    "DUPLICATE_IGNORED",
    "OTHER",
]);
export const paymentRefundStatuses = Object.freeze(["REQUESTED", "SETTLED", "FAILED"]);
export const paymentRefundReasonCodes = Object.freeze([
    "CUSTOMER_REQUEST",
    "SUPPLIER_UNAVAILABLE",
    "SERVICE_RECOVERY",
    "DUPLICATE_PAYMENT",
    "OTHER",
]);
export const paymentCancellationActorKinds = Object.freeze(["CUSTOMER", "STAFF"]);
/** The evidence that backs one status change of a payment request (history rows). */
export const paymentTransitionEvidenceKinds = Object.freeze([
    "SOURCE_VERIFIED",
    "PROVIDER_ORDER",
    "PROVIDER_OBSERVATION",
    "EXPIRY_CLOCK",
    "PROVIDER_STATUS_CHECK",
    "REFUND_OBSERVATION",
    "CUSTOMER_COMMAND",
    "STAFF_COMMAND",
]);
/** Who created a payment request. Administrators and catalog editors cannot (owner ruling Q3). */
export const paymentCreatorKinds = Object.freeze(["customer", "staff"]);
/**
 * The one place that says which currencies a payment request may be CREATED in (owner decision 5:
 * INR only). Storage keeps the full allow-list shape; the database guarantees that a request equals
 * its source instead of hard-coding a second copy of this constant.
 */
export const PAYMENT_REQUEST_CREATION_CURRENCIES = Object.freeze([currencyCode("INR")]);
/** A payment request is payable for at most this long (or until its source validity ends, if sooner). */
export const PAYMENT_REQUEST_TTL_MS = 24 * 60 * 60 * 1000;
/** A supplier quote is valid for at most 30 days (owner decision 5; no amount cap, no second approver). */
export const PAYMENT_QUOTE_MAX_VALIDITY_MS = 30 * 24 * 60 * 60 * 1000;
// --- The transition table (data) ------------------------------------------------------------------
export const paymentCommands = Object.freeze([
    "mark_ready",
    "open_checkout",
    "settle",
    "fail",
    "expire",
    "cancel",
    "refund_settled",
]);
const rule = (id, from, to, command, who = {}) => Object.freeze({
    id,
    from,
    to,
    command,
    customer: who.customer === true,
    staff: who.staff === true,
    system: Object.freeze([...(who.system ?? [])]),
});
const CHECKOUT = ["payment-checkout"];
const RECONCILIATION = ["payment-reconciliation"];
const EXPIRY = ["payment-expiry"];
/**
 * The eleven allowed status changes: the spec's nine plus READY to CANCELLED (P10) and READY to
 * EXPIRED (P11), which the owner approved (ruling Q1), as R23 to R26 were approved in Phase 2.3. Nine
 * statuses make 72 ordered pairs between different statuses, so 61 are refused. The database guard
 * (LF018) allows exactly these (from, to) pairs. Only the reconciliation service moves a request to
 * PAID or FAILED, and a client callback is never evidence for any of them. FAILED is final and so is
 * every other end state: a retry is a new request (`retryOfPaymentRequestId`).
 */
export const paymentRequestTransitions = Object.freeze([
    rule("P1", "DRAFT", "READY", "mark_ready", { customer: true, staff: true }),
    rule("P2", "READY", "PENDING_PAYMENT", "open_checkout", { system: CHECKOUT }),
    rule("P3", "PENDING_PAYMENT", "PAID", "settle", { system: RECONCILIATION }),
    rule("P4", "PENDING_PAYMENT", "FAILED", "fail", { system: RECONCILIATION }),
    rule("P5", "PENDING_PAYMENT", "EXPIRED", "expire", {
        system: ["payment-expiry", "payment-reconciliation"],
    }),
    rule("P6", "PENDING_PAYMENT", "CANCELLED", "cancel", { customer: true, staff: true }),
    rule("P7", "PAID", "PARTIALLY_REFUNDED", "refund_settled", { system: RECONCILIATION }),
    rule("P8", "PAID", "REFUNDED", "refund_settled", { system: RECONCILIATION }),
    rule("P9", "PARTIALLY_REFUNDED", "REFUNDED", "refund_settled", { system: RECONCILIATION }),
    rule("P10", "READY", "CANCELLED", "cancel", { customer: true, staff: true }),
    rule("P11", "READY", "EXPIRED", "expire", { system: EXPIRY }),
]);
/**
 * The one allowed event that keeps the status: a further partial refund settles on a request that is
 * already PARTIALLY_REFUNDED (the version moves, a history row is written, the refunded amount
 * grows). Every other same-status update is refused.
 */
export const paymentPartialRefundEvent = rule("P7b", "PARTIALLY_REFUNDED", "PARTIALLY_REFUNDED", "refund_settled", { system: RECONCILIATION });
/** The eleven (from, to) pairs between different statuses. */
export function isAllowedPaymentTransition(from, to) {
    return paymentRequestTransitions.some((row) => row.from === from && row.to === to);
}
/** The eleven pairs plus the one same-status refund event. */
export function isAllowedPaymentEvent(from, to) {
    return (isAllowedPaymentTransition(from, to) ||
        (from === paymentPartialRefundEvent.from && to === paymentPartialRefundEvent.to));
}
const isTerminal = (status) => paymentRequestTerminalStatuses.includes(status);
/**
 * The concierge request this payment request names has ended (COMPLETED, UNAVAILABLE, DECLINED,
 * CANCELLED or EXPIRED). Its offer is withdrawn: checkout cannot open, and a settlement of the order
 * is a reconciliation exception, never a PAID request (owner ruling Q5). An unlinked request is never
 * withdrawn.
 */
export const isRequestWithdrawn = (request) => request.conciergeRequestState !== null &&
    conciergeRequestTerminalStates.includes(request.conciergeRequestState);
const PROVIDER_FORMAT = /^[a-z][a-z0-9_]*$/;
const PROVIDER_ID_FORMAT = /^[!-~]{1,255}$/;
const sameMoney = (a, b) => a.amountMinor === b.amountMinor && a.currency === b.currency;
function invalidEvidence() {
    throw violation("payment_request.evidence_invalid");
}
/** The observation names this request's provider order. */
function namesOrderOf(request, observation) {
    return (request.provider !== null &&
        request.providerOrderId !== null &&
        observation.provider === request.provider &&
        observation.providerOrderId === request.providerOrderId);
}
function staffCapabilityFor(command, request) {
    switch (command) {
        case "mark_ready":
            return request.sourceType === "CATALOG_FIXED_PRICE"
                ? "payment.create_for_customer"
                : "payment.issue_quote_request";
        case "cancel":
            return "payment.cancel";
        default:
            return null;
    }
}
/**
 * The first check of a staff actor on a payment request, before any check of its status (R24): the
 * capability the command needs and the relationship that capability needs (a catalog request: an
 * effective assignment or the supervisor role; a request from a quote: the concierge request's owner
 * or a supervisor; a cancel: the owner, an effective assignment or a supervisor). Both depend on the
 * command and the request's source, never on the status of the PAYMENT request. The owner relationship
 * is read from the linked concierge request, so it holds only while that request is open (see
 * `relationsOf` in the application `staff-access` module). A staff user with the role and no relationship gets `relationship_missing` in
 * every payment status, so the payment status class is not revealed.
 */
function assertStaffReachesPayment(actor, capability, environment) {
    assertStaffCapability(actor, capability, environment);
    if (capability === "payment.create_for_customer") {
        assertStaffRelationship(actor.authority, actor.relationship, "CUSTOMER_DATA");
    }
    else if (capability === "payment.issue_quote_request") {
        if (!actor.relationship.isOwner && !isSupervisor(actor.authority)) {
            throw denied("relationship_missing");
        }
    }
    else if (!actor.relationship.isOwner &&
        !actor.relationship.hasEffectiveAssignment &&
        !isSupervisor(actor.authority)) {
        throw denied("relationship_missing");
    }
}
/**
 * Plans one status change of a payment request, or refuses it. Checks run in this order, so the
 * error is deterministic: (1) a customer must own the request (`not_owner`); (1b) for a staff actor
 * of a command that staff may drive, the capability and the relationship (see
 * `assertStaffReachesPayment`), so a staff user with no relationship gets the same refusal in every
 * state (R24); (2) the pair (`payment_request.terminal` from an end state, else
 * `payment_request.transition_invalid`; a command that does not belong to the pair is also invalid);
 * (3) the actor against the rule (a customer, a named system actor, or staff where the rule says so;
 * a staff actor of a command that staff may not drive is `role_missing`); (4) `expectedVersion`
 * (mandatory for staff); (5) the staff reason of a cancel; (6) the evidence of the command. Nothing
 * is written here.
 *
 * Only system `payment-reconciliation` settles (PAID) a request, and only on a SETTLED observation of
 * the request's own provider order whose amount and currency equal the request's. The plan never
 * touches a concierge request: a payment does not confirm one.
 */
export function planPaymentRequestTransition(input) {
    const { request, to, command, actor, evidence, now } = input;
    if (actor.kind === "customer" && actor.customerId !== request.customerId) {
        throw denied("not_owner");
    }
    if (actor.kind === "staff") {
        const capability = staffCapabilityFor(command, request);
        if (capability !== null)
            assertStaffReachesPayment(actor, capability, input.environment);
    }
    if (isTerminal(request.status))
        throw violation("payment_request.terminal");
    const matched = request.status === to
        ? command === paymentPartialRefundEvent.command &&
            request.status === paymentPartialRefundEvent.from
            ? paymentPartialRefundEvent
            : undefined
        : paymentRequestTransitions.find((row) => row.from === request.status && row.to === to);
    if (matched === undefined || matched.command !== command) {
        throw violation("payment_request.transition_invalid");
    }
    switch (actor.kind) {
        case "customer":
            if (!matched.customer)
                throw denied("role_missing");
            break;
        case "system":
            if (!matched.system.includes(actor.actorId))
                throw denied("role_missing");
            break;
        case "staff":
            // The capability and the relationship were checked first (`assertStaffReachesPayment`).
            if (!matched.staff || staffCapabilityFor(command, request) === null) {
                throw denied("role_missing");
            }
            break;
    }
    checkExpectedVersion(actor, input.expectedVersion, request.version, "payment_request.version_conflict");
    if (actor.kind === "staff" && command === "cancel")
        requireReason(input.reason);
    const effect = planEffect(request, to, command, actor, evidence, now);
    const history = {
        fromStatus: request.status,
        toStatus: to,
        actorKind: actor.kind,
        actorId: transitionActorId(actor),
        evidenceKind: effect.evidenceKind,
        evidenceObservationId: effect.observationId,
        evidenceRefundId: effect.refundId,
        internalReason: actor.kind === "staff" && command === "cancel" ? (input.reason ?? null) : null,
    };
    return Object.freeze({
        ruleId: matched.id,
        command,
        from: request.status,
        to,
        patch: effect.patch,
        history: Object.freeze(history),
        auditAction: effect.auditAction,
    });
}
function observationOf(evidence, kind, request) {
    if (evidence.kind !== "observation")
        return invalidEvidence();
    const { observation } = evidence;
    if (observation.kind !== kind || !namesOrderOf(request, observation))
        return invalidEvidence();
    return observation;
}
function planEffect(request, to, command, actor, evidence, now) {
    const pastExpiry = now.getTime() >= request.expiresAt.getTime();
    switch (command) {
        case "mark_ready": {
            if (evidence.kind !== "source_verified")
                return invalidEvidence();
            if (request.expiresAt.getTime() <= now.getTime())
                throw violation("payment_request.expired");
            return {
                patch: { kind: "mark_ready" },
                evidenceKind: "SOURCE_VERIFIED",
                observationId: null,
                refundId: null,
                auditAction: "payment_request.ready",
            };
        }
        case "open_checkout": {
            if (evidence.kind !== "provider_order")
                return invalidEvidence();
            if (request.providerOrderId !== null)
                throw violation("payment_request.provider_order_exists");
            if (request.expiresAt.getTime() <= now.getTime())
                throw violation("payment_request.expired");
            // The linked concierge request ended: the offer is withdrawn, so no money may be asked (Q5).
            if (isRequestWithdrawn(request))
                throw violation("payment_request.request_withdrawn");
            if (!PROVIDER_FORMAT.test(evidence.provider) ||
                !PROVIDER_ID_FORMAT.test(evidence.providerOrderId) ||
                (evidence.providerCustomerReference !== null &&
                    !PROVIDER_ID_FORMAT.test(evidence.providerCustomerReference))) {
                throw new DomainValidationError("providerOrder", "must be a provider and a visible-ASCII id");
            }
            return {
                patch: {
                    kind: "open_checkout",
                    provider: evidence.provider,
                    providerOrderId: evidence.providerOrderId,
                    providerCustomerReference: evidence.providerCustomerReference,
                    checkoutOpenedAt: "now",
                },
                evidenceKind: "PROVIDER_ORDER",
                observationId: null,
                refundId: null,
                auditAction: "payment_request.checkout_opened",
            };
        }
        case "settle": {
            const observation = observationOf(evidence, "SETTLED", request);
            // Amount and currency must equal the request's: no tolerance, no partial capture.
            if (observation.amount === null || !sameMoney(observation.amount, request.amount)) {
                return invalidEvidence();
            }
            return {
                patch: { kind: "settle", paidObservationId: observation.observationId, paidAt: "now" },
                evidenceKind: "PROVIDER_OBSERVATION",
                observationId: observation.observationId,
                refundId: null,
                auditAction: "payment_request.paid",
            };
        }
        case "fail": {
            const observation = observationOf(evidence, "FAILED", request);
            return {
                patch: { kind: "fail", failedAt: "now" },
                evidenceKind: "PROVIDER_OBSERVATION",
                observationId: observation.observationId,
                refundId: null,
                auditAction: "payment_request.failed",
            };
        }
        case "expire": {
            if (!pastExpiry)
                throw violation("payment_request.not_expired_yet");
            const patch = { kind: "expire", expiredAt: "now" };
            const auditAction = "payment_request.expired";
            if (actor.kind === "system" && actor.actorId === "payment-reconciliation") {
                const observation = observationOf(evidence, "ORDER_EXPIRED", request);
                return {
                    patch,
                    evidenceKind: "PROVIDER_OBSERVATION",
                    observationId: observation.observationId,
                    refundId: null,
                    auditAction,
                };
            }
            if (evidence.kind !== "status_check")
                return invalidEvidence();
            if (request.status === "READY") {
                // No provider order exists, so there is nothing that could have been paid.
                return {
                    patch,
                    evidenceKind: "EXPIRY_CLOCK",
                    observationId: null,
                    refundId: null,
                    auditAction,
                };
            }
            if (evidence.providerStatus !== "no_settlement") {
                throw violation("payment_request.settlement_possible");
            }
            return {
                patch,
                evidenceKind: "PROVIDER_STATUS_CHECK",
                observationId: null,
                refundId: null,
                auditAction,
            };
        }
        case "cancel": {
            if (evidence.kind === "withdrawn_resolution") {
                // The one cancel of a payment that may hold captured money: a staff member resolved its
                // REQUEST_WITHDRAWN exception as refunded outside Lafayette. Nothing else may use this evidence.
                if (actor.kind !== "staff" ||
                    request.status !== "PENDING_PAYMENT" ||
                    !isRequestWithdrawn(request) ||
                    evidence.exceptionKind !== "REQUEST_WITHDRAWN" ||
                    evidence.resolutionCode !== "REFUNDED_OUT_OF_BAND" ||
                    evidence.paymentRequestId !== request.id) {
                    return invalidEvidence();
                }
                return {
                    patch: { kind: "cancel", cancellationActorKind: "STAFF", cancelledAt: "now" },
                    evidenceKind: "STAFF_COMMAND",
                    observationId: evidence.observationId,
                    refundId: null,
                    auditAction: "payment_request.cancelled",
                };
            }
            if (evidence.kind !== "status_check")
                return invalidEvidence();
            // A request with a provider order may have been paid: only a provider "no settlement" cancels it.
            if (request.status === "PENDING_PAYMENT" && evidence.providerStatus !== "no_settlement") {
                throw violation("payment_request.settlement_possible");
            }
            return {
                patch: {
                    kind: "cancel",
                    cancellationActorKind: actor.kind === "customer" ? "CUSTOMER" : "STAFF",
                    cancelledAt: "now",
                },
                evidenceKind: actor.kind === "customer" ? "CUSTOMER_COMMAND" : "STAFF_COMMAND",
                observationId: null,
                refundId: null,
                auditAction: actor.kind === "customer"
                    ? "payment_request.cancelled_by_customer"
                    : "payment_request.cancelled",
            };
        }
        case "refund_settled": {
            if (evidence.kind !== "refund")
                return invalidEvidence();
            const { refund, observation } = evidence;
            if (refund.status !== "REQUESTED" ||
                observation.kind !== "REFUND_SETTLED" ||
                observation.amount === null ||
                !sameMoney(observation.amount, refund.amount) ||
                refund.amount.currency !== request.amount.currency ||
                refund.providerRefundId === null ||
                observation.providerRefundId !== refund.providerRefundId ||
                observation.provider !== refund.provider) {
                return invalidEvidence();
            }
            const total = request.refundedAmountMinor + refund.amount.amountMinor;
            if (total > request.amount.amountMinor)
                throw violation("payment_refund.exceeds_paid");
            const refunded = total === request.amount.amountMinor;
            // The target must be the one the sum implies: a full refund is REFUNDED, a part is PARTIALLY_REFUNDED.
            if ((to === "REFUNDED") !== refunded)
                throw violation("payment_request.transition_invalid");
            return {
                patch: {
                    kind: "refund_settled",
                    status: refunded ? "REFUNDED" : "PARTIALLY_REFUNDED",
                    refundedAmountMinor: minorUnits(total),
                },
                evidenceKind: "REFUND_OBSERVATION",
                observationId: observation.observationId,
                refundId: refund.refundId,
                auditAction: refunded ? "payment_request.refunded" : "payment_request.partially_refunded",
            };
        }
    }
}
/**
 * The expiry sweep's plan for one request (system `payment-expiry`): a READY request past its
 * `expiresAt` expires (P11); a PENDING_PAYMENT one expires (P5) only when the provider says there was
 * no settlement. A settled or unknown answer is `payment_request.settlement_possible`: ingest first.
 */
export function planPaymentExpiry(input) {
    return planPaymentRequestTransition({
        request: input.request,
        to: "EXPIRED",
        command: "expire",
        actor: { kind: "system", actorId: "payment-expiry" },
        expectedVersion: input.expectedVersion ?? null,
        reason: null,
        evidence: { kind: "status_check", providerStatus: input.providerStatus },
        now: input.now,
        environment: input.environment,
    });
}
/**
 * A cancel by the customer owner, the concierge request's owner, an assigned concierge or a
 * supervisor (the last three with a reason): READY cancels directly (P10), PENDING_PAYMENT only with a
 * provider answer of `no_settlement` (P6). The one other P6 is the cancel that resolving a
 * REQUEST_WITHDRAWN exception causes (`withdrawn_resolution` evidence, see `planReconciliationResolution`).
 */
export function planPaymentCancel(input) {
    return planPaymentRequestTransition({
        request: input.request,
        to: "CANCELLED",
        command: "cancel",
        actor: input.actor,
        expectedVersion: input.expectedVersion,
        reason: input.reason,
        evidence: { kind: "status_check", providerStatus: input.providerStatus },
        now: input.now,
        environment: input.environment,
    });
}
/** A `payment_request.price_not_eligible` refusal that keeps the eligibility code for the rejected outcome. */
export class PaymentPriceNotEligibleError extends DomainRuleViolationError {
    eligibilityCode;
    constructor(eligibilityCode) {
        super("payment_request.price_not_eligible");
        this.name = "PaymentPriceNotEligibleError";
        this.eligibilityCode = eligibilityCode;
    }
}
const contextInvalid = () => violation("payment_request.context_invalid");
/** The context links of a new payment request, each judged: the customer's own, usable, and consistent with each other. */
export function paymentContextLinks(context, catalogItemId) {
    const { conciergeRequest, trip, itineraryItem, retryOf } = context;
    if (conciergeRequest !== null) {
        if (conciergeRequest.customerId !== context.customerId ||
            conciergeRequestTerminalStates.includes(conciergeRequest.state) ||
            (conciergeRequest.catalogItemId !== null &&
                catalogItemId !== null &&
                conciergeRequest.catalogItemId !== catalogItemId)) {
            throw contextInvalid();
        }
    }
    if (trip !== null && trip.customerId !== context.customerId)
        throw contextInvalid();
    if (itineraryItem !== null) {
        if (itineraryItem.customerId !== context.customerId ||
            (trip !== null && itineraryItem.tripId !== trip.id) ||
            (itineraryItem.catalogItemId !== null &&
                catalogItemId !== null &&
                itineraryItem.catalogItemId !== catalogItemId)) {
            throw contextInvalid();
        }
    }
    if (retryOf !== null) {
        if (retryOf.customerId !== context.customerId ||
            !["FAILED", "EXPIRED", "CANCELLED"].includes(retryOf.status)) {
            throw contextInvalid();
        }
    }
    return {
        conciergeRequestId: conciergeRequest?.id ?? null,
        tripId: trip?.id ?? null,
        itineraryItemId: itineraryItem?.id ?? null,
        retryOfPaymentRequestId: retryOf?.id ?? null,
    };
}
/** `min(now + TTL, validity end)`, in milliseconds like the database column. */
export function paymentRequestExpiry(now, validUntil) {
    const byTtl = now.getTime() + PAYMENT_REQUEST_TTL_MS;
    return new Date(validUntil === null ? byTtl : Math.min(byTtl, validUntil.getTime()));
}
/**
 * Who may create a payment request, and the creator stamp and channel it records: the customer owner
 * (the app, and the agent while it holds the AI lease, which the application enforces with the fence),
 * an assigned concierge or a supervisor. An administrator and a catalog editor cannot (ruling Q3), and
 * a system actor never creates one.
 */
export function paymentCreator(actor, customerId, capability, environment) {
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== customerId)
                throw denied("not_owner");
            return {
                createdBy: { kind: "customer", id: actor.customerId },
                createdChannel: actor.channel,
                history: {
                    actorKind: "customer",
                    actorId: actor.customerId,
                    evidenceKind: "CUSTOMER_COMMAND",
                },
            };
        case "staff": {
            assertStaffReachesPayment(actor, capability, environment);
            return {
                createdBy: { kind: "staff", id: actor.authority.staffUserId },
                createdChannel: null,
                history: {
                    actorKind: "staff",
                    actorId: actor.authority.staffUserId,
                    evidenceKind: "STAFF_COMMAND",
                },
            };
        }
        case "system":
            throw denied("role_missing");
    }
}
/** The two history rows and audit actions of a creation, which both creation plans share. */
export function paymentCreationRecord(history) {
    const createdHistory = Object.freeze({
        fromStatus: null,
        toStatus: "DRAFT",
        ...history,
        evidenceObservationId: null,
        evidenceRefundId: null,
        internalReason: null,
    });
    const readyHistory = Object.freeze({
        fromStatus: "DRAFT",
        toStatus: "READY",
        actorKind: history.actorKind,
        actorId: history.actorId,
        evidenceKind: "SOURCE_VERIFIED",
        evidenceObservationId: null,
        evidenceRefundId: null,
        internalReason: null,
    });
    return {
        readyPatch: Object.freeze({ kind: "mark_ready" }),
        createdHistory,
        readyHistory,
        auditActions: ["payment_request.created", "payment_request.ready"],
    };
}
/**
 * Known-price creation: the customer (app, or the agent under the AI lease), an assigned concierge or
 * a supervisor chooses WHAT to pay for, never how much. The input has no amount, no currency and no
 * description: the snapshot comes from Postgres in this transaction, `resolveEligibleFixedPrice` judges
 * it with the transaction clock and INR only (`PAYMENT_REQUEST_CREATION_CURRENCIES`), and the
 * amount, currency, item id, item version, authorization time and description are copied from the
 * decision (the item's name is the description). The database guard LF019 verifies the same equality at
 * INSERT and at DRAFT to READY. Not eligible: `payment_request.price_not_eligible`, with the
 * eligibility code on the error (`PaymentPriceNotEligibleError`), not in its message.
 */
export function planCatalogPaymentRequest(input) {
    const creator = paymentCreator(input.actor, input.context.customerId, "payment.create_for_customer", input.environment);
    const decision = resolveEligibleFixedPrice(input.snapshot, input.now, PAYMENT_REQUEST_CREATION_CURRENCIES);
    if (!decision.eligible)
        throw new PaymentPriceNotEligibleError(decision.code);
    const links = paymentContextLinks(input.context, decision.catalogItemId);
    const draft = plannedPaymentRequestDraft({
        customerId: input.context.customerId,
        sourceType: "CATALOG_FIXED_PRICE",
        sourceCatalogItemId: decision.catalogItemId,
        sourceCatalogItemVersion: decision.catalogItemVersion,
        sourcePriceAuthorizedAt: decision.authorizedAt,
        sourceQuoteId: null,
        amount: decision.price,
        description: boundedText("description", input.catalogName, 200),
        ...links,
        catalogItemId: decision.catalogItemId,
        createdBy: creator.createdBy,
        createdChannel: creator.createdChannel,
        expiresAt: paymentRequestExpiry(input.now, input.snapshot.validUntil),
    });
    return Object.freeze({ draft, ...paymentCreationRecord(creator.history) });
}
/**
 * A refund request: a supervisor or an administrator (`payment.refund`, aal2 in production by
 * `authorizeStaff`), by payment request id and with no customer-read capability (ruling Q4), with a
 * required reason and reason code. The request must be PAID or PARTIALLY_REFUNDED, in its currency,
 * and requested plus settled refunds (`reservedMinor`; failed ones are free) plus this one must not
 * exceed the paid amount. The refund row is a request: the payment request changes only when a
 * REFUND_SETTLED observation matches it.
 */
export function planPaymentRefund(input) {
    const staff = assertStaffCapability(input.actor, "payment.refund", input.environment);
    const reason = requireReason(input.reason);
    if (!isMember(paymentRefundReasonCodes, input.reasonCode)) {
        throw new DomainValidationError("reasonCode", "must be a known refund reason code");
    }
    if (input.request.status !== "PAID" && input.request.status !== "PARTIALLY_REFUNDED") {
        throw violation("payment_refund.not_refundable");
    }
    if (input.amount.amountMinor <= 0) {
        throw new DomainValidationError("amountMinor", "must be above zero");
    }
    if (input.amount.currency !== input.request.amount.currency) {
        throw new DomainValidationError("currency", "must be the currency of the payment");
    }
    if (input.reservedMinor + input.amount.amountMinor > input.request.amount.amountMinor) {
        throw violation("payment_refund.exceeds_paid");
    }
    return Object.freeze({
        refund: Object.freeze({
            paymentRequestId: input.request.id,
            customerId: input.request.customerId,
            amount: input.amount,
            reasonCode: input.reasonCode,
            requestedByStaffUserId: staff.authority.staffUserId,
        }),
        reason,
        auditAction: "payment_refund.requested",
    });
}
